// A group's members are positioned LOCALLY within their group (1, 2, 3...
// relative to the group), not as part of the form's overall position
// sequence — so a naive `.sort((a, b) => a.position - b.position)` over the
// full flat question list interleaves members into the wrong place (a
// member with local position 1 sorts to the very front of the whole form).
//
// This produces the correct order instead: top-level items (ordinary
// questions + group containers) sorted by their own position, with each
// group's members immediately following it, sorted by their own local
// position. A no-op re-sort when no question has a groupQuestionId (every
// form without a group).
type GroupableQuestion = {
  id: string;
  position: number;
  groupQuestionId?: string | null;
  type?: string;
};

export type GroupedQuestionItem<T> = {
  question: T;
  members: T[];
};

const collectMembersByGroupId = <T extends GroupableQuestion>(
  questions: T[]
) => {
  const membersByGroupId = new Map<string, T[]>();
  questions.forEach((q) => {
    if (q.groupQuestionId) {
      const members = membersByGroupId.get(q.groupQuestionId) ?? [];
      members.push(q);
      membersByGroupId.set(q.groupQuestionId, members);
    }
  });
  return membersByGroupId;
};

export const sortQuestionsWithGroups = <T extends GroupableQuestion>(
  questions: T[]
): T[] => {
  if (!questions?.length) return questions ?? [];

  const membersByGroupId = collectMembersByGroupId(questions);

  if (membersByGroupId.size === 0) {
    return questions.slice().sort((a, b) => a.position - b.position);
  }

  const topLevel = questions
    .filter((q) => !q.groupQuestionId)
    .slice()
    .sort((a, b) => a.position - b.position);

  const result: T[] = [];
  topLevel.forEach((q) => {
    result.push(q);
    if (q.type === "group") {
      const members = (membersByGroupId.get(q.id) ?? []).sort(
        (a, b) => a.position - b.position
      );
      result.push(...members);
    }
  });
  return result;
};

// Nests members under their group so screens can render a single section
// instead of a flat list. Members whose group is missing from this list
// stay as top-level items so they are never dropped from the UI.
export const groupQuestionsForDisplay = <T extends GroupableQuestion>(
  questions: T[]
): GroupedQuestionItem<T>[] => {
  if (!questions?.length) return [];

  const membersByGroupId = collectMembersByGroupId(questions);
  const nestedMemberIds = new Set<string>();

  const topLevel = questions
    .filter((q) => !q.groupQuestionId)
    .slice()
    .sort((a, b) => a.position - b.position);

  const result: GroupedQuestionItem<T>[] = topLevel.map((q) => {
    const members =
      q.type === "group"
        ? (membersByGroupId.get(q.id) ?? []).sort(
            (a, b) => a.position - b.position
          )
        : [];
    members.forEach((member) => nestedMemberIds.add(member.id));
    return { question: q, members };
  });

  questions.forEach((q) => {
    if (q.groupQuestionId && !nestedMemberIds.has(q.id)) {
      result.push({ question: q, members: [] });
    }
  });

  return result;
};
